import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import type { Section } from "../types/objects.types";

type BackendSection = {
	id: string;
	blockId: string;
	name: string;
	isDeleted?: boolean;
};

type BackendResponse<T> = {
	success: true;
	data: T;
};

type SectionsListResponse = {
	items: BackendSection[];
	meta: {
		limit: number;
		page: number;
		total: number;
		totalPages: number;
	};
};

export type SectionCreatePayload = {
	name: string;
	blockId: string;
};

export type SectionsListParams = {
	blockId?: string;
	limit?: number;
	page?: number;
};

const mapSection = (section: BackendSection): Section => ({
	id: section.id,
	blockId: section.blockId,
	name: section.name,
	roomsCount: 0, // TODO: Update from backend aggregation when available
});

export const sectionsApi = {
	getSections: async (params: SectionsListParams = {}): Promise<Section[]> => {
		const response = await apiClient.get<BackendResponse<SectionsListResponse>>(
			endpoints.objects.sections,
			{ params }
		);
		return response.data.data.items.filter((section) => !section.isDeleted).map(mapSection);
	},

	getSection: async (id: string): Promise<Section> => {
		const response = await apiClient.get<BackendResponse<BackendSection>>(
			endpoints.objects.sectionDetail(id)
		);
		return mapSection(response.data.data);
	},

	createSection: async (payload: SectionCreatePayload): Promise<Section> => {
		const response = await apiClient.post<BackendResponse<BackendSection>>(
			endpoints.objects.sections,
			payload
		);
		return mapSection(response.data.data);
	},

	updateSection: async (id: string, payload: Partial<SectionCreatePayload>): Promise<Section> => {
		const response = await apiClient.patch<BackendResponse<BackendSection>>(
			endpoints.objects.sectionDetail(id),
			payload
		);
		return mapSection(response.data.data);
	},

	deleteSection: async (id: string): Promise<void> => {
		await apiClient.delete(endpoints.objects.sectionDetail(id));
	},
};
